import { Stack } from '@mui/material';
import Papa from 'papaparse';
import { ChangeEvent, Dispatch, SetStateAction, useRef } from 'react';
import { getSnakeCaseItems } from 'src/helper/getSnakeCaseItem';
import { useAlerting, useTranslation } from 'src/hooks';
import useSystemCurrency from 'src/hooks/useSystemCurrency';
import { useAppSelector } from 'src/reducer/hooks';
import { selectProfile } from 'src/reducer/profileSlice';
import { bigNumStrMulitpleDecimals } from 'src/utils';
import useValidation from '../hook/useValidation';
import { CsvData } from '../types/CsvData';
import { Preview } from '../types/Preview';
import useNullOrExceedDataChecking from '../hook/useNullOrExceedDataChecking';

type Props = {
  previews: Preview[];
  setFileName: Dispatch<SetStateAction<string | undefined>>;
  setPreviews: Dispatch<SetStateAction<Preview[]>>;
};

const FileSelector = (props: Props) => {
  const { previews, setPreviews, setFileName } = props;

  const uuidRef = useRef<string | null>(null);
  if (!uuidRef.current) {
    uuidRef.current = crypto.randomUUID();
  }
  const batchReference = `BATCH-${uuidRef.current}`;

  const { jwtDetails } = useAppSelector(selectProfile);
  const { programAgentId } = jwtDetails || {};

  const { handleNullOrExceedRecords } = useNullOrExceedDataChecking();
  const { t } = useTranslation('clientCreditAllocation');
  const { alerting } = useAlerting();
  const { validateItem } = useValidation();

  const { SystemCurrencyEnum } = useSystemCurrency();

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !file.name) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        //check Null or excedd Records
        const { alertMsg } = handleNullOrExceedRecords(results);
        if (alertMsg) {
          alerting('error', alertMsg);
          return;
        }

        //clear previous items before handling new item
        if (previews.length !== 0) {
          setPreviews([]);
        }

        setFileName(file.name);

        //change csv date to snake case
        const snakeCaseItems = getSnakeCaseItems<CsvData>(results.data);

        for (let i = 0; i < snakeCaseItems.length; i++) {
          const item = snakeCaseItems[i];

          try {
            const { isValidationPassed, errorMessage } = validateItem(item);
            if (!isValidationPassed) {
              throw new Error(errorMessage);
            }

            const creditCurrencyDecimals: number = item.credit_currency
              ? JSON.parse(SystemCurrencyEnum[item.credit_currency]).decimals
              : 0;

            const preview: Preview = {
              originalRowNumber: i,
              ...item,
              validationPass: true,
              paramData: {
                programAgentId: programAgentId,
                batchReference: batchReference,
                customerNumber: item.pfh_no,
                creditCurrency: item.credit_currency,
                creditAmount: item.credit_amount
                  ? bigNumStrMulitpleDecimals(item.credit_amount, creditCurrencyDecimals)
                  : '',
                creditCurrencyDecimals,
              },
            };
            setPreviews((prev) => [...prev, preview]);
          } catch (error) {
            const msg = error instanceof Error ? error.message : `${t('unknownError')}, `;
            const errorResponse: Preview = {
              originalRowNumber: i,
              ...item,
              validationPass: false,
              validationError: `${t('dataRowNo')} ${i + 2}, ${msg}`.slice(0, -2),
            };
            setPreviews((prev) => [...prev, errorResponse]);
          }
        }
        //force to trigger same file upload
        event.target.value = '';
      },
      error: (error) => {
        console.error('Error parsing CSV:', error);
      },
    });
  };

  return (
    <Stack flexShrink={1}>
      <input
        type="file"
        id="files"
        accept=".csv"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
      <label
        htmlFor="files"
        style={{
          cursor: 'pointer',
          border: '1px solid',
          borderRadius: '4px',
          padding: '5px 15px',
          fontSize: '0.6875rem',
          fontWeight: '500',
          width: 'fit-content',
        }}
      >
        {t('selectAFile')}
      </label>
    </Stack>
  );
};

export default FileSelector;
